import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnboardingTourService } from '../services/onboarding-tour.service.js';
import { TwitchChatStateService } from '../services/twitch-chat-state.service.js';
import { DemoService } from '../services/demo.service.js';
import { FirebaseService } from '../services/firebase.service.js';

interface TargetRect {
  x: number;
  y: number;
  width: number;
  height: number;
  found: boolean;
}

interface SpotlightSet {
  primary: TargetRect; // also used for tooltip positioning (first match of primary anchor)
  extras: TargetRect[]; // additional cut-outs for related areas (no border)
}

/** Find all elements matching a tour anchor (data-tour="X" with multi-value support via attr~=). */
function queryAll(anchor: string): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  // attr~=value matches "X", "X Y", "Y X", "Y X Z" — any whitespace-separated token list containing X.
  const list = document.querySelectorAll<HTMLElement>(`[data-tour~="${anchor}"]`);
  return Array.from(list);
}

function rectOf(el: HTMLElement): TargetRect {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height, found: r.width > 0 && r.height > 0 };
}

interface TooltipPos {
  top: number;
  left: number;
  arrow: 'top' | 'bottom' | 'left' | 'right' | 'none';
  centred: boolean;
}

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 16;
const TOOLTIP_WIDTH = 360;
const TOOLTIP_MAX_HEIGHT = 280;

@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'recompute()',
    '(window:scroll)': 'recompute()',
    '(document:click)': 'onDocumentClick($event)',
  },
  template: `
@if (tour.isActive()) {
  @if (tour.currentStep(); as step) {
    @if (spotlightSet(); as set) {
      <!-- Spotlight overlay (SVG with cut-out) -->
      <svg class="fixed inset-0 w-screen h-screen pointer-events-none"
           [style.z-index]="10000"
           xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tour-spotlight">
            <rect width="100%" height="100%" fill="white"/>
            @if (set.primary.found) {
              <rect [attr.x]="set.primary.x - 8"
                    [attr.y]="set.primary.y - 8"
                    [attr.width]="set.primary.width + 16"
                    [attr.height]="set.primary.height + 16"
                    rx="8" ry="8"
                    fill="black"/>
            }
            @for (extra of set.extras; track $index) {
              <rect [attr.x]="extra.x - 4"
                    [attr.y]="extra.y - 4"
                    [attr.width]="extra.width + 8"
                    [attr.height]="extra.height + 8"
                    rx="6" ry="6"
                    fill="black"/>
            }
          </mask>
        </defs>
        <rect width="100%" height="100%"
              fill="rgba(0,0,0,0.55)"
              mask="url(#tour-spotlight)"/>
        @if (set.primary.found) {
          <rect [attr.x]="set.primary.x - 8"
                [attr.y]="set.primary.y - 8"
                [attr.width]="set.primary.width + 16"
                [attr.height]="set.primary.height + 16"
                rx="8" ry="8"
                fill="none"
                stroke="rgba(129, 140, 248, 0.85)"
                stroke-width="2"
                class="tour-pulse"/>
        }
      </svg>

      <!-- Tooltip card -->
      @if (tooltipPos(); as pos) {
        <div class="fixed bg-gray-900 border border-indigo-500/50 rounded-xl shadow-2xl shadow-black/60 animate-fade-in"
             [style.z-index]="10001"
             [style.top.px]="pos.top"
             [style.left.px]="pos.left"
             [style.width.px]="tooltipWidth"
             [class.translate-x--half]="pos.centred">
          <div class="p-4 flex flex-col gap-2">
            <div class="flex items-baseline justify-between gap-2">
              <h3 class="text-base font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-300">
                {{ step.title }}
              </h3>
              <span class="text-[10px] text-gray-500 shrink-0 font-mono">
                {{ tour.currentStepIndex() + 1 }} / {{ tour.totalSteps() }}
              </span>
            </div>
            <p class="text-sm text-gray-300 leading-relaxed">{{ step.body }}</p>

            @if (step.cta; as cta) {
              <button (click)="onCta(cta.action)"
                      class="w-full mt-2 py-2.5 px-4 bg-[#9146FF] hover:bg-[#7b32e6] text-white text-sm font-bold rounded-lg transition-transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-[#9146FF]/20">
                @if (cta.action === 'login') {
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
                }
                {{ cta.label }}
              </button>
            }

            <div class="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-800 flex-wrap">
              <div class="flex items-center gap-2">
                <button (click)="tour.skip()"
                        class="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 transition-colors">
                  Пропустить тур
                </button>
                @if (step.expectedAction !== 'next' && !tour.actionComplete()) {
                  <span class="text-gray-700">·</span>
                  <button (click)="tour.skipStep()"
                          class="text-xs text-gray-500 hover:text-amber-400 px-2 py-1 transition-colors">
                    Пропустить шаг
                  </button>
                }
              </div>

              <div class="flex items-center gap-1">
                <button (click)="tour.prev()"
                        [disabled]="tour.isFirstStep()"
                        class="px-3 py-1.5 text-sm rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  ← Назад
                </button>

                @if (step.expectedAction === 'next') {
                  <button (click)="tour.next()"
                          class="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-colors">
                    @if (tour.isLastStep()) { Готово } @else { Далее → }
                  </button>
                } @else {
                  <button (click)="tour.next()"
                          [disabled]="!tour.actionComplete()"
                          [class.bg-indigo-600]="tour.actionComplete()"
                          [class.hover:bg-indigo-500]="tour.actionComplete()"
                          [class.bg-gray-800]="!tour.actionComplete()"
                          [class.text-gray-500]="!tour.actionComplete()"
                          [class.cursor-not-allowed]="!tour.actionComplete()"
                          class="px-3 py-1.5 text-sm rounded-lg text-white font-semibold transition-colors">
                    @if (tour.actionComplete()) {
                      @if (tour.isLastStep()) { Готово } @else { Далее → }
                    } @else {
                      Выполните действие
                    }
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      }
    }
  }
}
  `,
  styles: [`
    @keyframes tourPulse {
      0%, 100% { stroke-opacity: 0.85; }
      50% { stroke-opacity: 0.35; }
    }
    .tour-pulse {
      animation: tourPulse 2s ease-in-out infinite;
    }
    .translate-x--half {
      transform: translateX(-50%);
    }
  `],
})
export class TourOverlayComponent implements OnDestroy {
  tour = inject(OnboardingTourService);
  private state = inject(TwitchChatStateService);
  private demoService = inject(DemoService);
  private firebaseService = inject(FirebaseService);
  tooltipWidth = TOOLTIP_WIDTH;
  private resizeObs: ResizeObserver | null = null;
  private mutationObs: MutationObserver | null = null;
  private pollTimer: any = null;

  /** Forces recompute on demand (window resize / scroll / step change). */
  private recomputeTrigger = signal<number>(0);

  spotlightSet = computed<SpotlightSet | null>(() => {
    if (!this.tour.isActive()) return null;
    const step = this.tour.currentStep();
    this.recomputeTrigger(); // depend on trigger
    if (!step) return null;
    if (step.anchor === null) {
      return { primary: { x: 0, y: 0, width: 0, height: 0, found: false }, extras: [] };
    }
    if (typeof document === 'undefined') return null;
    const primaryMatches = queryAll(step.anchor);
    const primary: TargetRect = primaryMatches.length > 0
      ? rectOf(primaryMatches[0])
      : { x: 0, y: 0, width: 0, height: 0, found: false };
    if (!primary.found && primaryMatches.length === 0) {
      console.warn(`[tour] target [data-tour~="${step.anchor}"] not found, falling back to centred tooltip`);
    }

    // Extras = all-but-first of the primary anchor (e.g. "show all ai cards" — primary is one with border, rest plain)
    //       + every match of each additionalAnchor.
    const extras: TargetRect[] = [];
    for (let i = 1; i < primaryMatches.length; i++) {
      const r = rectOf(primaryMatches[i]);
      if (r.found) extras.push(r);
    }
    for (const a of step.additionalAnchors ?? []) {
      const matches = queryAll(a);
      for (const el of matches) {
        const r = rectOf(el);
        if (r.found) extras.push(r);
      }
    }
    return { primary, extras };
  });

  targetRect = computed<TargetRect | null>(() => this.spotlightSet()?.primary ?? null);

  tooltipPos = computed<TooltipPos | null>(() => {
    const rect = this.targetRect();
    if (!rect) return null;
    const step = this.tour.currentStep();
    if (!step) return null;

    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 720;

    // No-anchor / not-found → centred tooltip
    if (!rect.found) {
      return {
        top: Math.max(40, vh / 2 - TOOLTIP_MAX_HEIGHT / 2),
        left: vw / 2,
        arrow: 'none',
        centred: true,
      };
    }

    // Try preferred position, flip if doesn't fit
    const positions = step.position === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : [step.position, this.opposite(step.position), 'bottom', 'top'];

    for (const pos of positions) {
      const result = this.tryPosition(pos as 'top'|'bottom'|'left'|'right', rect, vw, vh);
      if (result) return result;
    }

    // Fallback: center
    return {
      top: Math.max(40, vh / 2 - TOOLTIP_MAX_HEIGHT / 2),
      left: vw / 2,
      arrow: 'none',
      centred: true,
    };
  });

  constructor() {
    // Re-measure target whenever the active step or active flag changes.
    effect(() => {
      this.tour.currentStep();
      this.tour.isActive();
      // Also depend on highlights array + selected card so spotlight follows
      // card additions/removals (slider, AI) and expansion of the focused card.
      this.state.highlights();
      this.state.currentHighlightIndex();
      this.state.searchBinMatches();
      this.demoService.signInPromptVisible();
      // Wait for setup() to mount/show target before measuring.
      queueMicrotask(() => this.recompute());
      setTimeout(() => this.recompute(), 50);
      setTimeout(() => this.recompute(), 200);
      setTimeout(() => this.recompute(), 600);
      this.attachObservers();
    });
  }

  ngOnDestroy(): void {
    this.detachObservers();
  }

  private attachObservers(): void {
    this.detachObservers();
    if (typeof window === 'undefined') return;
    if (!this.tour.isActive()) return;

    // ResizeObserver: track the primary anchor + all extras so spotlight rect
    // expands when the focused card grows (description un-clamped on selection).
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.recompute());
      const step = this.tour.currentStep();
      if (step?.anchor) {
        for (const el of queryAll(step.anchor)) this.resizeObs.observe(el);
      }
      for (const a of step?.additionalAnchors ?? []) {
        for (const el of queryAll(a)) this.resizeObs.observe(el);
      }
    }

    // MutationObserver on body: catches dynamically-rendered targets (popovers,
    // newly-added AI cards). Recomputes when subtree changes — debounced via
    // microtask so we don't thrash on every DOM tick.
    let pending = false;
    this.mutationObs = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      queueMicrotask(() => {
        pending = false;
        this.recompute();
      });
    });
    this.mutationObs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tour'] });
  }

  private detachObservers(): void {
    if (this.resizeObs) { this.resizeObs.disconnect(); this.resizeObs = null; }
    if (this.mutationObs) { this.mutationObs.disconnect(); this.mutationObs = null; }
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  }

  recompute(): void {
    this.recomputeTrigger.update(n => n + 1);
  }

  onDocumentClick(event: Event): void {
    this.tour.notifyDocumentClick(event);
    // Force re-measure on the next tick — covers cases where a click triggers
    // a popover/modal that we want to include in the spotlight.
    setTimeout(() => this.recompute(), 16);
    setTimeout(() => this.recompute(), 100);
  }

  async onCta(action: 'login' | 'restart' | 'finish'): Promise<void> {
    if (action === 'login') {
      this.tour.complete();
      try {
        await this.firebaseService.loginWithTwitch();
        // After successful login, drop ?demo=1 so user lands in the real app.
        if (this.demoService.isDemoMode() && typeof window !== 'undefined') {
          window.location.assign(window.location.pathname);
        }
      } catch (err) {
        console.error('[tour] login failed', err);
      }
    } else if (action === 'restart') {
      this.tour.restart();
    } else {
      this.tour.complete();
    }
  }

  private opposite(pos: 'top' | 'bottom' | 'left' | 'right' | 'auto'): 'top' | 'bottom' | 'left' | 'right' {
    switch (pos) {
      case 'top': return 'bottom';
      case 'bottom': return 'top';
      case 'left': return 'right';
      case 'right': return 'left';
      default: return 'bottom';
    }
  }

  private tryPosition(
    pos: 'top' | 'bottom' | 'left' | 'right',
    rect: TargetRect,
    vw: number,
    vh: number,
  ): TooltipPos | null {
    let top = 0, left = 0;
    switch (pos) {
      case 'bottom':
        top = rect.y + rect.height + TOOLTIP_GAP;
        left = rect.x + rect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case 'top':
        top = rect.y - TOOLTIP_MAX_HEIGHT - TOOLTIP_GAP;
        left = rect.x + rect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case 'right':
        top = rect.y + rect.height / 2 - TOOLTIP_MAX_HEIGHT / 2;
        left = rect.x + rect.width + TOOLTIP_GAP;
        break;
      case 'left':
        top = rect.y + rect.height / 2 - TOOLTIP_MAX_HEIGHT / 2;
        left = rect.x - TOOLTIP_WIDTH - TOOLTIP_GAP;
        break;
    }
    // Clamp to viewport with 16px margin
    const margin = 16;
    const fitsHoriz = left >= margin && (left + TOOLTIP_WIDTH) <= (vw - margin);
    const fitsVert = top >= margin && (top + TOOLTIP_MAX_HEIGHT) <= (vh - margin);
    if (!fitsHoriz || !fitsVert) {
      // Try clamping rather than failing outright — looks better than flipping for moderate overflow.
      const clampedLeft = Math.max(margin, Math.min(left, vw - TOOLTIP_WIDTH - margin));
      const clampedTop = Math.max(margin, Math.min(top, vh - TOOLTIP_MAX_HEIGHT - margin));
      // Accept if clamped position doesn't overlap target by more than a bit
      const horizOverlap = !(clampedLeft + TOOLTIP_WIDTH < rect.x || clampedLeft > rect.x + rect.width);
      const vertOverlap = !(clampedTop + TOOLTIP_MAX_HEIGHT < rect.y || clampedTop > rect.y + rect.height);
      if (horizOverlap && vertOverlap) return null; // would obscure target
      return { top: clampedTop, left: clampedLeft, arrow: pos, centred: false };
    }
    return { top, left, arrow: pos, centred: false };
  }
}
